import React, { useState, useEffect } from 'react';
import {
  Table, Button, Tag, Switch, Input, Modal, message, Typography, Empty, Result,
  Descriptions, Space, Tooltip, Card, Row, Col, Select, Spin, Divider,
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, FileTextOutlined,
  SaveOutlined, LinkOutlined, PlusOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  getFarmacias,
  updateFarmaciaEstado,
  getGescomFarmacia,
  vincularFarmacia,
  getCandidatosFarmaciaGescom,
  crearFarmaciaGescom,
  completarFarmaciaGescom,
  getProvinciasGescom,
  getMunicipiosGescom,
  ESTADO_PENDIENTE,
  ESTADO_REALIZADO,
  ESTADO_DESCARTADO,
  type ReconciliacionFarmacia,
  type GescomFarmaciaData,
  type CandidatoFarmaciaGescom,
  type ResultadoVinculacionFarmacia,
  type CrearFarmaciaGescomRequest,
  type ResultadoCreacionFarmacia,
  type ProvinciaGescom,
  type MunicipioGescom,
} from '../../api/reconciliacionApi';
import { fct } from '../../fct/tokens/tokens';
import { FctAviso, FctEstado, FctGlifoAtencion, claseFilaAtencion } from '../../fct/react';
import { EstadoOperacion, TipoOperacion } from './semantica';

const { Text } = Typography;
const { TextArea } = Input;

const estadoLabels: Record<number, { label: string }> = {
  0: { label: 'Pendiente' },
  1: { label: 'Realizado' },
  2: { label: 'Descartado' },
};

interface FarmaciasTabProps {
  selectedRecIds?: number[];
}

const FarmaciasTab: React.FC<FarmaciasTabProps> = ({ selectedRecIds }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState<ReconciliacionFarmacia[]>([]);
  const [filteredData, setFilteredData] = useState<ReconciliacionFarmacia[]>([]);
  const [showRealizados, setShowRealizados] = useState(false);
  const [searchText, setSearchText] = useState('');

  // Modal detalle
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ReconciliacionFarmacia | null>(null);
  const [editNotas, setEditNotas] = useState('');
  const [editEstadoId, setEditEstadoId] = useState<number>(ESTADO_PENDIENTE);
  const [actionLoading, setActionLoading] = useState(false);

  // Gescom farmacia lookup
  const [gescomIdInput, setGescomIdInput] = useState('');
  const [gescomData, setGescomData] = useState<GescomFarmaciaData | null>(null);
  const [gescomLoading, setGescomLoading] = useState(false);
  const [gescomError, setGescomError] = useState<string | null>(null);

  // Vinculación, y cómo quedó la última de este detalle (#174)
  const [vinculando, setVinculando] = useState(false);
  const [resultadoVinculacion, setResultadoVinculacion] = useState<ResultadoVinculacionFarmacia | null>(null);

  // Buscar en Gescom (GDO-T39)
  const [buscarVisible, setBuscarVisible] = useState(false);
  const [buscarTexto, setBuscarTexto] = useState('');
  const [candidatos, setCandidatos] = useState<CandidatoFarmaciaGescom[]>([]);
  const [buscandoCandidatos, setBuscandoCandidatos] = useState(false);
  const [busquedaHecha, setBusquedaHecha] = useState(false);

  // Crear en Gescom y vincular en un paso (#175)
  const [crearVisible, setCrearVisible] = useState(false);
  const [crearDatos, setCrearDatos] = useState<CrearFarmaciaGescomRequest>({ direccion: '', poblacion: '', provinciaId: 0, idMunicipio: 0 });
  const [creando, setCreando] = useState(false);
  const [crearResultado, setCrearResultado] = useState<ResultadoCreacionFarmacia | null>(null);
  const [provincias, setProvincias] = useState<ProvinciaGescom[]>([]);
  const [municipios, setMunicipios] = useState<MunicipioGescom[]>([]);
  const [municipiosLoading, setMunicipiosLoading] = useState(false);

  const loadData = async () => {
    setLoading(true);
    setError(false);
    try {
      const estadoFilter = showRealizados ? undefined : ESTADO_PENDIENTE;
      let result: ReconciliacionFarmacia[];
      if (!selectedRecIds || selectedRecIds.length === 0) {
        result = await getFarmacias(undefined, estadoFilter);
      } else {
        const results = await Promise.all(selectedRecIds.map(rid => getFarmacias(rid, estadoFilter)));
        result = results.flat();
      }
      setData(result);
      applySearch(result, searchText);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [showRealizados, selectedRecIds]);

  const applySearch = (items: ReconciliacionFarmacia[], text: string) => {
    if (!text.trim()) {
      setFilteredData(items);
      return;
    }
    const lower = text.toLowerCase();
    setFilteredData(items.filter(f =>
      (f.farmaciaGescom?._Name || '').toLowerCase().includes(lower) ||
      (f.direccion || '').toLowerCase().includes(lower) ||
      (f.localidad || '').toLowerCase().includes(lower) ||
      (f.provincia || '').toLowerCase().includes(lower) ||
      (f.licenciaSOE || '').toLowerCase().includes(lower)
    ));
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    applySearch(data, value);
  };

  const handleUpdateEstado = async (id: number, estadoId: number, notasGestion?: string) => {
    setActionLoading(true);
    try {
      await updateFarmaciaEstado(id, estadoId, notasGestion);
      const label = estadoLabels[estadoId]?.label || 'actualizada';
      message.success(`Farmacia ${label.toLowerCase()} correctamente`);
      setModalVisible(false);
      loadData();
    } catch {
      message.error('Error al actualizar la farmacia');
    } finally {
      setActionLoading(false);
    }
  };

  // Cargar datos de Gescom para una farmacia
  const loadGescomData = async (farmaciaGsId: number) => {
    setGescomLoading(true);
    setGescomData(null);
    setGescomError(null);
    try {
      const data = await getGescomFarmacia(farmaciaGsId);
      setGescomData(data);
    } catch {
      setGescomError('No se encontró la farmacia en Gescom o error de conexión');
    } finally {
      setGescomLoading(false);
    }
  };

  // Buscar farmacia en Gescom por ID introducido manualmente
  const handleBuscarGescom = () => {
    const id = parseInt(gescomIdInput, 10);
    if (isNaN(id) || id <= 0) {
      setGescomError('Introduce un ID numérico válido');
      return;
    }
    loadGescomData(id);
  };

  // Vincular registros (#174): en Gescom y, después, en CU2. La respuesta dice cómo quedó; un rechazo llega como 409.
  const handleVincular = async () => {
    if (!selectedItem || !gescomData) return;
    setVinculando(true);
    setResultadoVinculacion(null);
    try {
      const result = await vincularFarmacia(selectedItem.id, gescomData.id);
      setResultadoVinculacion(result);

      // Recargar la ficha de Gescom, que ya apunta a la farmacia de CU2.
      try {
        setGescomData(await getGescomFarmacia(gescomData.id));
      } catch {
        // Si falla la recarga, no bloqueamos
      }

      // En Gescom queda vinculada en los tres casos: el item local lo refleja sin cerrar el modal
      const updated = { ...selectedItem, idFarmaciaGS: gescomData.id };
      setSelectedItem(updated);
      setData(prev => prev.map(f => f.id === updated.id ? updated : f));
      setFilteredData(prev => prev.map(f => f.id === updated.id ? updated : f));
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Error al vincular los registros';
      message.error(msg);
    } finally {
      setVinculando(false);
    }
  };

  // --- Buscar en Gescom (GDO-T39; en Gescom, con IDs de Gescom, desde #173) ---

  const openBuscar = () => {
    setBuscarTexto([selectedItem?.direccion, selectedItem?.localidad].filter(Boolean).join(' '));
    setCandidatos([]);
    setBusquedaHecha(false);
    setBuscarVisible(true);
  };

  const handleBuscarCandidatos = async () => {
    if (buscarTexto.trim().length < 3) {
      message.warning('Escribe al menos 3 caracteres para buscar');
      return;
    }
    setBuscandoCandidatos(true);
    try {
      const result = await getCandidatosFarmaciaGescom(buscarTexto.trim());
      setCandidatos(result);
      setBusquedaHecha(true);
    } catch (err: any) {
      // Un fallo no es «sin candidatos»: se dice, y no queda a la vista la lista anterior.
      setCandidatos([]);
      setBusquedaHecha(false);
      message.error(err?.response?.data?.message || 'Error al buscar candidatos en Gescom');
    } finally {
      setBuscandoCandidatos(false);
    }
  };

  const handleSeleccionCandidato = (c: CandidatoFarmaciaGescom) => {
    setBuscarVisible(false);
    setGescomIdInput(String(c.id));
    loadGescomData(c.id);
  };

  // --- Crear en Gescom (GDO-T39) ---

  // Normalización para casar la localidad CU2 con el nombre de municipio Gescom: sin tildes ni mayúsculas.
  const normalizarTexto = (s?: string) =>
    (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase().trim();

  const handleProvinciaChange = async (provinciaId: number, localidadCU2?: string) => {
    setCrearDatos(prev => ({ ...prev, provinciaId, idMunicipio: 0 }));
    setMunicipios([]);
    setMunicipiosLoading(true);
    try {
      const ms = await getMunicipiosGescom(provinciaId);
      setMunicipios(ms);
      // Preselección si la localidad CU2 coincide exactamente con un municipio (el usuario puede cambiarla)
      const match = ms.find(m => normalizarTexto(m.nombre) === normalizarTexto(localidadCU2));
      if (match) setCrearDatos(prev => ({ ...prev, idMunicipio: match.id }));
    } catch {
      message.error('Error al cargar los municipios de la provincia');
    } finally {
      setMunicipiosLoading(false);
    }
  };

  const openCrear = async () => {
    // Un alta a medias de esta fila se completa, no se repite: el modal vuelve a abrir su resultado.
    if (crearResultado?.estado === 'a-medias') {
      setCrearVisible(true);
      return;
    }
    const item = selectedItem;
    setCrearDatos({
      direccion: item?.direccion || '',
      poblacion: item?.localidad || '',
      cp: item?.cp,
      provinciaId: 0,
      idMunicipio: 0,
      licenciaSOE: item?.licenciaSOE,
      telefono: item?.telefono,
    });
    setCrearResultado(null);
    setMunicipios([]);
    setCrearVisible(true);
    try {
      const res = await getProvinciasGescom(item?.provincia);
      setProvincias(res.provincias);
      if (res.sugeridaId) {
        await handleProvinciaChange(res.sugeridaId, item?.localidad);
      }
    } catch {
      message.error('Error al cargar el catálogo de provincias');
    }
  };

  // El resultado de crear o completar: vinculada, la fila lo refleja y el card "Datos Gescom" carga la farmacia creada.
  const aplicarCreacion = (result: ResultadoCreacionFarmacia) => {
    setCrearResultado(result);
    if (!selectedItem || result.estado !== 'vinculado') return;
    setResultadoVinculacion({ estado: 'vinculado', mensaje: result.mensaje, idFarmacia: result.idFarmacia });
    const updated = { ...selectedItem, idFarmaciaGS: result.idFarmacia };
    setSelectedItem(updated);
    setData(prev => prev.map(f => f.id === updated.id ? updated : f));
    setFilteredData(prev => prev.map(f => f.id === updated.id ? updated : f));
    setGescomIdInput(String(result.idFarmacia));
    loadGescomData(result.idFarmacia);
  };

  // Crear y vincular (#175). Un rechazo antes del alta llega como 400, 404 o 409 y no crea nada.
  const handleCrear = async () => {
    if (!selectedItem) return;
    setCreando(true);
    try {
      aplicarCreacion(await crearFarmaciaGescom(selectedItem.id, crearDatos));
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Error al crear la farmacia en Gescom';
      message.error(msg);
    } finally {
      setCreando(false);
    }
  };

  // Completar sobre la farmacia ya creada lo que quedó a medias, sin crear otra (el alta no es idempotente).
  const handleCompletar = async () => {
    if (!selectedItem || !crearResultado) return;
    setCreando(true);
    try {
      aplicarCreacion(await completarFarmaciaGescom(selectedItem.id, crearResultado.idFarmacia, { licenciaSOE: crearDatos.licenciaSOE }));
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Error al completar la farmacia en Gescom';
      message.error(msg);
    } finally {
      setCreando(false);
    }
  };

  const openDetail = (item: ReconciliacionFarmacia) => {
    setSelectedItem(item);
    setEditNotas(item.notasGestion || '');
    setEditEstadoId(item.estadoId);
    setGescomIdInput('');
    setResultadoVinculacion(null);
    setCrearResultado(null);
    setGescomData(null);
    setGescomError(null);
    setModalVisible(true);

    // Si es ALTA y ya tiene idFarmaciaGS, cargar automáticamente datos Gescom
    if (item.tipoOperacion === 'ALTA' && item.idFarmaciaGS) {
      loadGescomData(item.idFarmaciaGS);
    }
  };

  const pendingCount = data.filter(f => f.estadoId === ESTADO_PENDIENTE).length;

  const columns: ColumnsType<ReconciliacionFarmacia> = [
    {
      title: 'Rec',
      dataIndex: 'reconciliacionId',
      key: 'rec',
      className: 'fct-num',
      align: 'right',
      width: 45,
      render: (val: number) => <Text type="secondary" style={{ fontSize: 12 }}>{val}</Text>,
    },
    {
      title: 'Tipo',
      dataIndex: 'tipoOperacion',
      key: 'tipo',
      width: 80,
      filters: [
        { text: 'ALTA', value: 'ALTA' },
        { text: 'BAJA', value: 'BAJA' },
      ],
      onFilter: (value, record) => record.tipoOperacion === value,
      render: (val: string) => <TipoOperacion alta={val === 'ALTA'} etiqueta={val} />,
    },
    {
      title: 'Farmacia',
      key: 'nombre',
      render: (_, r) => r.farmaciaGescom?._Name || r.direccion || `CU2 ID ${r.idFarmaciaCU2 || r.idFarmaciaGS}`,
      ellipsis: true,
      width: 280,
    },
    {
      title: 'Provincia',
      dataIndex: 'provincia',
      key: 'provincia',
      width: 130,
      sorter: (a, b) => (a.provincia || '').localeCompare(b.provincia || '', 'es'),
      showSorterTooltip: false,
    },
    {
      title: 'Localidad',
      dataIndex: 'localidad',
      key: 'localidad',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Fecha',
      dataIndex: 'fechaCreacion',
      key: 'fecha',
      className: 'fct-num',
      align: 'right',
      width: 110,
      render: (val: string) => new Date(val).toLocaleDateString('es-ES'),
    },
    {
      title: 'Relaciones',
      key: 'relaciones',
      width: 90,
      render: (_, r) => r.relacionesPendientes ? (
        <Tooltip title="Tiene titularidades/empleados pendientes"><span><FctGlifoAtencion nivel="atencion" /> Si</span></Tooltip>
      ) : <Text type="secondary">—</Text>,
    },
    {
      title: 'Estado',
      dataIndex: 'estadoId',
      key: 'estado',
      width: 100,
      render: (val: number) => <EstadoOperacion estadoId={val} etiqueta={estadoLabels[val]?.label || `Estado ${val}`} />,
    },
    {
      title: '',
      key: 'notas',
      width: 40,
      render: (_, r) => r.notasGestion ? (
        <Tooltip title={r.notasGestion}><FileTextOutlined /></Tooltip>
      ) : null,
    },
  ];

  if (error) {
    return (
      <Result
        status="error"
        title="Error al cargar farmacias"
        extra={<Button type="primary" onClick={loadData} icon={<ReloadOutlined />}>Reintentar</Button>}
      />
    );
  }

  // Render del modal ALTA con comparación CU2/Gescom
  const renderAltaModal = (item: ReconciliacionFarmacia) => (
    <div>
      {/* Relaciones pendientes — aviso superior */}
      {item.relacionesPendientes && (
        <div style={{ background: fct.estado.aviso.relleno, border: `1px solid ${fct.estado.aviso.linea}`, borderRadius: fct.radio.control, padding: 12, marginBottom: 16 }}>
          <FctGlifoAtencion nivel="atencion" />{' '}
          <Text strong style={{ color: fct.estado.aviso.texto }}>Titularidades / empleados pendientes de esta farmacia</Text>
          <div style={{ marginTop: 4 }}>
            <Text style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{item.relacionesPendientes}</Text>
          </div>
        </div>
      )}

      {/* Dos columnas: CU2 (izq) | Gescom (der) */}
      <Row gutter={16}>
        {/* ─── Columna CU2 ─── */}
        <Col xs={24} md={12}>
          <Card
            title="Datos CU2"
            size="small"
            style={{ marginBottom: 16 }}
          >
            <Descriptions size="small" column={1} colon={false} labelStyle={{ fontWeight: 500, width: 120, color: fct.tinta['700'] }}>
              <Descriptions.Item label="id_farmacia">
                <Text code>{item.idFarmaciaCU2 ?? '—'}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="id_farmacia_gs">
                {item.idFarmaciaGS
                  ? <Text code>{item.idFarmaciaGS}</Text>
                  : <Text type="secondary">sin vincular</Text>
                }
              </Descriptions.Item>
              <Descriptions.Item label="Direccion">{item.direccion || '—'}</Descriptions.Item>
              <Descriptions.Item label="Poblacion">{item.localidad || '—'}</Descriptions.Item>
              <Descriptions.Item label="C.Postal"><span className="fct-mono">{item.cp || '—'}</span></Descriptions.Item>
              <Descriptions.Item label="Provincia">{item.provincia || '—'}</Descriptions.Item>
              <Descriptions.Item label="Licencia SOE"><span className="fct-mono">{item.licenciaSOE || '—'}</span></Descriptions.Item>
              <Descriptions.Item label="Telefono"><span className="fct-mono">{item.telefono || '—'}</span></Descriptions.Item>
            </Descriptions>
          </Card>
          {!item.idFarmaciaGS && (
            <Space style={{ marginBottom: 16 }}>
              <Button icon={<SearchOutlined />} onClick={openBuscar}>Buscar en Gescom</Button>
              <Button icon={<PlusOutlined />} onClick={openCrear}>Crear en Gescom</Button>
            </Space>
          )}
        </Col>

        {/* ─── Columna Gescom ─── */}
        <Col xs={24} md={12}>
          <Card
            title="Datos Gescom"
            size="small"
            style={{ marginBottom: 16 }}
          >
            <Spin spinning={gescomLoading}>
              <Descriptions size="small" column={1} colon={false} labelStyle={{ fontWeight: 500, width: 130, color: fct.tinta['700'] }}>
                <Descriptions.Item label="id_farmacia_CU2">
                  {gescomData?.idFarmaciaCU2
                    ? <Text code>{gescomData.idFarmaciaCU2}</Text>
                    : <Text type="secondary">{gescomData ? 'sin asignar' : '—'}</Text>
                  }
                </Descriptions.Item>
                <Descriptions.Item label="ID">
                  {item.idFarmaciaGS ? (
                    <>
                      <Text code>{item.idFarmaciaGS}</Text>
                      <Button
                        size="small"
                        icon={<ReloadOutlined />}
                        onClick={() => loadGescomData(item.idFarmaciaGS!)}
                        loading={gescomLoading}
                        style={{ marginLeft: 8 }}
                      />
                    </>
                  ) : (
                    <>
                      <Space.Compact size="small">
                        <Input
                          placeholder="ID"
                          value={gescomData ? String(gescomData.id) : gescomIdInput}
                          onChange={e => { setGescomIdInput(e.target.value); setGescomData(null); }}
                          onPressEnter={handleBuscarGescom}
                          style={{ width: 100 }}
                          disabled={!!gescomData}
                        />
                        <Button
                          icon={<ReloadOutlined />}
                          onClick={() => gescomData ? loadGescomData(gescomData.id) : handleBuscarGescom()}
                          loading={gescomLoading}
                          size="small"
                        />
                      </Space.Compact>
                      {gescomError && (
                        <Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>{gescomError}</Text>
                      )}
                    </>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Direccion">
                  {/* Sin enlace a Gedeco: el ID ya es de Gescom, que Gedeco no conoce (#149, #151). */}
                  {gescomData?.direccion || <Text type="secondary">—</Text>}
                </Descriptions.Item>
                <Descriptions.Item label="Poblacion">{gescomData?.localidad || <Text type="secondary">—</Text>}</Descriptions.Item>
                <Descriptions.Item label="C.Postal"><span className="fct-mono">{gescomData?.cp || <Text type="secondary">—</Text>}</span></Descriptions.Item>
                <Descriptions.Item label="Provincia">{gescomData?.provincia || <Text type="secondary">—</Text>}</Descriptions.Item>
                <Descriptions.Item label="Licencia SOE"><span className="fct-mono">{gescomData?.licenciaSOE != null ? String(gescomData.licenciaSOE) : <Text type="secondary">—</Text>}</span></Descriptions.Item>
                <Descriptions.Item label="Telefono"><span className="fct-mono">{gescomData?.telefono || <Text type="secondary">—</Text>}</span></Descriptions.Item>
              </Descriptions>
            </Spin>
          </Card>
        </Col>
      </Row>

      {/* Zona de vinculación */}
      {item.idFarmaciaGS ? (
        <div style={{ background: fct.estado.exito.relleno, border: `1px solid ${fct.estado.exito.linea}`, borderRadius: fct.radio.control, padding: 12, marginBottom: 16, textAlign: 'center' }}>
          <FctEstado personalizado={{ etiqueta: 'Registros vinculados', familia: 'exito', icono: 'check' }} />
        </div>
      ) : gescomData && item.estadoId === ESTADO_PENDIENTE
        && (!gescomData.idFarmaciaCU2 || gescomData.idFarmaciaCU2 === item.idFarmaciaCU2) ? (
        <div style={{ background: fct.estado.exito.relleno, border: `1px solid ${fct.estado.exito.linea}`, borderRadius: fct.radio.control, padding: 16, marginBottom: 16, textAlign: 'center' }}>
          {gescomData.idFarmaciaCU2 != null && gescomData.idFarmaciaCU2 === item.idFarmaciaCU2 && (
            <div style={{ marginBottom: 8 }}>
              <FctGlifoAtencion nivel="atencion" />{' '}
              <Text type="warning">
                Vinculación a medias: la farmacia Gescom ya apunta a esta farmacia CU2, pero falta el id_farmacia_gs en CU2.
              </Text>
            </div>
          )}
          <Button
            type="primary"
            size="large"
            icon={<LinkOutlined />}
            loading={vinculando}
            onClick={() => {
              Modal.confirm({
                width: 520,
                title: 'Vincular registros',
                content: (
                  <div>
                    <p>Se vinculará la farmacia de CU2 con el registro de Gescom:</p>
                    <ul style={{ margin: '8px 0' }}>
                      <li><strong>CU2:</strong> id_farmacia {item.idFarmaciaCU2} — {item.direccion || item.localidad}</li>
                      <li><strong>Gescom:</strong> ID {gescomData.id} — {gescomData.direccion || gescomData.localidad}</li>
                    </ul>
                    <p>La vinculación se registrará en ambas bases de datos. ¿Continuar?</p>
                  </div>
                ),
                okText: 'Vincular',
                cancelText: 'Cancelar',
                onOk: handleVincular,
              });
            }}
          >
            Vincular registros
          </Button>
        </div>
      ) : null}

      {/* Cómo quedó la vinculación: en los dos lados, o a medias, con su reintento (#178). */}
      {resultadoVinculacion && (
        <div style={{ marginBottom: 16 }}>
          <FctAviso
            tipo={resultadoVinculacion.estado === 'vinculado' ? 'exito' : 'error'}
            titulo={resultadoVinculacion.mensaje}
          >
            {resultadoVinculacion.estado === 'a-medias' && (
              <Button size="small" icon={<LinkOutlined />} loading={vinculando} onClick={handleVincular} style={{ marginTop: 8 }}>
                Volver a vincular
              </Button>
            )}
          </FctAviso>
        </div>
      )}

      <Divider style={{ margin: '12px 0' }} />

      {/* Comentario */}
      <Text strong style={{ display: 'block', marginBottom: 8 }}>Comentario</Text>
      <TextArea
        value={editNotas}
        onChange={e => setEditNotas(e.target.value)}
        autoSize={{ minRows: 2, maxRows: 5 }}
        placeholder="Anadir comentario..."
        style={{ marginBottom: 16 }}
      />

      {/* Acciones: estado selector + Guardar */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Select
          value={editEstadoId}
          onChange={setEditEstadoId}
          style={{ width: 160 }}
        >
          <Select.Option value={ESTADO_PENDIENTE}><FctEstado personalizado={{ etiqueta: 'Pendiente', familia: 'aviso', icono: 'hora' }} /></Select.Option>
          <Select.Option value={ESTADO_REALIZADO}><FctEstado personalizado={{ etiqueta: 'Realizado', familia: 'exito' }} /></Select.Option>
          <Select.Option value={ESTADO_DESCARTADO}><FctEstado personalizado={{ etiqueta: 'Descartado', familia: 'neutro', icono: 'cancelado' }} /></Select.Option>
        </Select>

        <Button
          icon={<SaveOutlined />}
          onClick={() => handleUpdateEstado(item.id, editEstadoId, editNotas || undefined)}
          loading={actionLoading}
        >
          Guardar
        </Button>
      </div>
    </div>
  );

  // Render del modal BAJA (mantiene el formato existente)
  const renderBajaModal = (item: ReconciliacionFarmacia) => (
    <div>
      {/* Relaciones pendientes — aviso superior */}
      {item.relacionesPendientes && (
        <div style={{ background: fct.estado.aviso.relleno, border: `1px solid ${fct.estado.aviso.linea}`, borderRadius: fct.radio.control, padding: 12, marginBottom: 16 }}>
          <FctGlifoAtencion nivel="atencion" />{' '}
          <Text strong style={{ color: fct.estado.aviso.texto }}>Titularidades / empleados pendientes de esta farmacia</Text>
          <div style={{ marginTop: 4 }}>
            <Text style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{item.relacionesPendientes}</Text>
          </div>
        </div>
      )}

      {/* Datos del caso (Gescom) — en las BAJA no hay registro CU2; los datos provienen de Gescom */}
      <Descriptions title="Datos de la farmacia (Gescom)" bordered size="small" column={2} style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Licencia SOE"><span className="fct-mono">{item.licenciaSOE || '-'}</span></Descriptions.Item>
        <Descriptions.Item label="Telefono"><span className="fct-mono">{item.telefono || '-'}</span></Descriptions.Item>
        <Descriptions.Item label="Codigo postal"><span className="fct-mono">{item.cp || '-'}</span></Descriptions.Item>
        <Descriptions.Item label="Direccion" span={2}>{item.direccion || '-'}</Descriptions.Item>
        <Descriptions.Item label="Localidad">{item.localidad || '-'}</Descriptions.Item>
        <Descriptions.Item label="Provincia">{item.provincia || '-'}</Descriptions.Item>
        {item.farmaciaGescom && (
          <Descriptions.Item label="Farmacia Gescom" span={2}>
            {item.farmaciaGescom._Name} (ID: <span className="fct-mono">{item.idFarmaciaGS}</span>)
          </Descriptions.Item>
        )}
      </Descriptions>

      {/* Accion */}
      {item.estadoId === ESTADO_PENDIENTE && (
        <div style={{ background: fct.superficie.hundida, border: `1px solid ${fct.linea.fila}`, borderRadius: fct.radio.control, padding: 16, marginBottom: 16 }}>
          <Text strong>Accion: confirmar baja tras gestion manual en Gescom</Text>
          <div style={{ marginTop: 12 }}>
            <Button
              type="primary"
              loading={actionLoading}
              onClick={() => {
                Modal.confirm({
                  title: 'Confirmar baja',
                  content: 'Confirma que has aplicado la baja en Gescom.',
                  okText: 'Confirmar',
                  cancelText: 'Cancelar',
                  onOk: () => handleUpdateEstado(item.id, ESTADO_REALIZADO, editNotas || undefined),
                });
              }}
            >
              Confirmar baja
            </Button>
          </div>
        </div>
      )}

      {/* Comentario */}
      <Text strong style={{ display: 'block', marginBottom: 8 }}>Comentario</Text>
      <TextArea
        value={editNotas}
        onChange={e => setEditNotas(e.target.value)}
        autoSize={{ minRows: 2, maxRows: 5 }}
        placeholder="Anadir comentario..."
        disabled={item.estadoId !== ESTADO_PENDIENTE}
        style={{ marginBottom: 16 }}
      />
      {item.estadoId === ESTADO_PENDIENTE && editNotas !== (item.notasGestion || '') && (
        <Button onClick={() => handleUpdateEstado(item.id, item.estadoId, editNotas || undefined)} loading={actionLoading}>
          Guardar comentario
        </Button>
      )}
    </div>
  );

  return (
    <div>
      {/* Cabecera */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <Text strong><span className="fct-mono">{pendingCount}</span> pendientes</Text>
        <div style={{ flex: 1 }} />
        <Switch
          checked={showRealizados}
          onChange={setShowRealizados}
          checkedChildren="Todos"
          unCheckedChildren="Solo pendientes"
        />
        <Input
          placeholder="Buscar por nombre, licencia SOE..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={e => handleSearch(e.target.value)}
          style={{ width: 250 }}
          allowClear
        />
        <Button icon={<ReloadOutlined />} onClick={loadData}>Refrescar</Button>
      </div>

      {/* Tabla */}
      <Table
        dataSource={filteredData.map(f => ({ ...f, key: f.id }))}
        columns={columns}
        loading={loading}
        pagination={{ defaultPageSize: 100, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], showTotal: total => `${total} registros` }}
        onRow={record => ({
          onClick: () => openDetail(record),
          style: { cursor: 'pointer' },
        })}
        rowClassName={(r) => claseFilaAtencion(r.relacionesPendientes ? 'atencion' : 'normal')}
        size="middle"
        scroll={{ x: 1000 }}
        locale={{ emptyText: <Empty description="No hay farmacias pendientes de conciliacion" /> }}
      />

      {/* Modal de detalle */}
      <Modal
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        title={selectedItem ? `${selectedItem.tipoOperacion === 'ALTA' ? 'Alta' : 'Baja'} de farmacia` : ''}
        width={selectedItem?.tipoOperacion === 'ALTA' ? 900 : 700}
        destroyOnClose
      >
        {selectedItem && (
          selectedItem.tipoOperacion === 'ALTA'
            ? renderAltaModal(selectedItem)
            : renderBajaModal(selectedItem)
        )}
      </Modal>

      {/* Sub-modal: Buscar farmacia en Gescom (GDO-T39) */}
      <Modal
        open={buscarVisible}
        onCancel={() => setBuscarVisible(false)}
        footer={null}
        title="Buscar farmacia en Gescom"
        width={920}
        destroyOnClose
      >
        <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
          <Input
            value={buscarTexto}
            onChange={e => setBuscarTexto(e.target.value)}
            onPressEnter={handleBuscarCandidatos}
            placeholder="Dirección y población, licencia SOE, CIF o teléfono"
            allowClear
            autoFocus
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            loading={buscandoCandidatos}
            onClick={handleBuscarCandidatos}
          >
            Buscar
          </Button>
        </Space.Compact>
        <Table
          size="small"
          dataSource={candidatos.map(c => ({ ...c, key: c.id }))}
          loading={buscandoCandidatos}
          pagination={false}
          scroll={{ y: 360 }}
          onRow={c => ({
            onClick: () => handleSeleccionCandidato(c),
            style: { cursor: 'pointer' },
          })}
          locale={{
            emptyText: (
              <Empty description={busquedaHecha
                ? 'Sin candidatos para ese texto'
                : 'Edita el texto si lo necesitas y pulsa Buscar'} />
            ),
          }}
          columns={[
            {
              title: 'Simil.',
              key: 'score',
              width: 70,
              render: (_, c: CandidatoFarmaciaGescom) => (
                <FctEstado personalizado={{ etiqueta: `${c.score}%`, familia: c.score >= 85 ? 'exito' : c.score >= 60 ? 'aviso' : 'neutro' }} />
              ),
            },
            { title: 'Dirección', dataIndex: 'direccion', key: 'direccion', ellipsis: true },
            { title: 'Localidad', dataIndex: 'localidad', key: 'localidad', width: 130, ellipsis: true },
            { title: 'Provincia', dataIndex: 'provincia', key: 'provincia', width: 110, ellipsis: true },
            {
              title: 'Código',
              key: 'codigo',
              width: 100,
              render: (_, c: CandidatoFarmaciaGescom) => <Text code>{c.codigo || c.id}</Text>,
            },
            {
              title: 'Estado',
              key: 'estado',
              width: 130,
              render: (_, c: CandidatoFarmaciaGescom) => (
                <Space size={4} wrap>
                  {c.idFarmaciaCU2 && (
                    <Tooltip title={`Ya vinculada a la farmacia CU2 ${c.idFarmaciaCU2} — posible duplicado`}>
                      <span><FctGlifoAtencion nivel="urgente" /> <span className="fct-codigo">CU2 {c.idFarmaciaCU2}</span></span>
                    </Tooltip>
                  )}
                  {c.fechaBaja && (
                    <Tooltip title={`Dada de baja en Gescom el ${new Date(c.fechaBaja).toLocaleDateString('es-ES')}`}>
                      <Tag style={{ marginInlineEnd: 0 }}>Baja {new Date(c.fechaBaja).toLocaleDateString('es-ES')}</Tag>
                    </Tooltip>
                  )}
                  {!c.idFarmaciaCU2 && !c.fechaBaja && <Text type="secondary">—</Text>}
                </Space>
              ),
            },
          ]}
        />
        <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
          Haz click en un candidato para cargarlo en el card "Datos Gescom" del caso.
        </Text>
      </Modal>

      {/* Sub-modal: Crear farmacia en Gescom y vincularla en un paso (#175) */}
      <Modal
        open={crearVisible}
        onCancel={() => setCrearVisible(false)}
        footer={null}
        title="Crear farmacia en Gescom"
        width={520}
        destroyOnClose
      >
        {!crearResultado ? (
          <div>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              Verifica los datos con los que se creará la farmacia en Gescom. Quedará vinculada a esta farmacia de CU2:
            </Text>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <Text style={{ width: 110, fontWeight: 500, color: fct.tinta['700'] }}>id_farmacia</Text>
              <Text code>{selectedItem?.idFarmaciaCU2}</Text>
            </div>
            {([
              ['Dirección *', 'direccion'],
              ['Población *', 'poblacion'],
              ['C.Postal', 'cp'],
            ] as [string, 'direccion' | 'poblacion' | 'cp'][]).map(([label, campo]) => (
              <div key={campo} style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <Text style={{ width: 110, fontWeight: 500, color: fct.tinta['700'] }}>{label}</Text>
                <Input
                  value={crearDatos[campo] || ''}
                  onChange={e => setCrearDatos(prev => ({ ...prev, [campo]: e.target.value }))}
                  style={{ flex: 1 }}
                />
              </div>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <Text style={{ width: 110, fontWeight: 500, color: fct.tinta['700'] }}>Provincia *</Text>
              <Select
                value={crearDatos.provinciaId || undefined}
                onChange={value => handleProvinciaChange(value, selectedItem?.localidad)}
                placeholder="Selecciona..."
                style={{ flex: 1 }}
                showSearch
                optionFilterProp="label"
                options={provincias.map(p => ({ value: p.id, label: p.nombre }))}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <Text style={{ width: 110, fontWeight: 500, color: fct.tinta['700'] }}>Municipio *</Text>
              <Select
                value={crearDatos.idMunicipio || undefined}
                onChange={value => setCrearDatos(prev => ({ ...prev, idMunicipio: value }))}
                placeholder={crearDatos.provinciaId ? 'Selecciona...' : 'Selecciona antes la provincia'}
                style={{ flex: 1 }}
                showSearch
                optionFilterProp="label"
                loading={municipiosLoading}
                disabled={!crearDatos.provinciaId}
                options={municipios.map(m => ({ value: m.id, label: m.nombre }))}
              />
            </div>
            {([
              ['Licencia SOE', 'licenciaSOE'],
              ['Teléfono', 'telefono'],
            ] as [string, 'licenciaSOE' | 'telefono'][]).map(([label, campo]) => (
              <div key={campo} style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <Text style={{ width: 110, fontWeight: 500, color: fct.tinta['700'] }}>{label}</Text>
                <Input
                  value={crearDatos[campo] || ''}
                  onChange={e => setCrearDatos(prev => ({ ...prev, [campo]: e.target.value }))}
                  style={{ flex: 1 }}
                />
              </div>
            ))}
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                loading={creando}
                disabled={!crearDatos.direccion.trim() || !crearDatos.poblacion.trim() || !crearDatos.provinciaId || !crearDatos.idMunicipio}
                onClick={() => {
                  Modal.confirm({
                    title: 'Crear farmacia en Gescom',
                    content: `Se creará la farmacia "${crearDatos.direccion} - ${crearDatos.poblacion}" en Gescom y se vinculará a esta farmacia de CU2. ¿Continuar?`,
                    okText: 'Crear',
                    cancelText: 'Cancelar',
                    onOk: handleCrear,
                  });
                }}
              >
                Crear registro
              </Button>
            </div>
          </div>
        ) : (
          <Result
            status={crearResultado.estado === 'vinculado' ? 'success' : 'warning'}
            title={crearResultado.estado === 'vinculado' ? 'Farmacia creada y vinculada' : 'Farmacia creada a medias'}
            subTitle={
              <>
                <div>{crearResultado.mensaje}</div>
                <div>
                  Farmacia de Gescom:{' '}
                  <Text code copyable style={{ fontSize: 16 }}>{crearResultado.idFarmacia}</Text>
                </div>
              </>
            }
            extra={[
              ...(crearResultado.estado === 'a-medias' ? [
                <Button key="completar" type="primary" icon={<LinkOutlined />} loading={creando} onClick={handleCompletar}>
                  Completar la vinculación
                </Button>,
              ] : []),
              <Button key="cerrar" onClick={() => setCrearVisible(false)}>
                Cerrar
              </Button>,
            ]}
          />
        )}
      </Modal>
    </div>
  );
};

export default FarmaciasTab;
